import React from 'react';

import dayjs, { dateDisplayFormat, dateTimeDisplayFormat } from '~libs/dayjs';

import { Button } from '~components';
import Icon, { faCircleCheck, faInfoCircle, faTriangleExclamation } from '~components/icon';

import { isClaimLodged } from '~support-at-home/lib/claimStatus';
import { Claim, ClaimApiPushResult } from '~support-at-home/models';

import HoverPopover from '~support-at-home/components/common/HoverPopover';

interface ComponentProps {
  claim: Claim;
  // The Send / Sync action, rendered by the parent and placed here rather than in the page toolbar.
  //
  // Passed as a NODE rather than an `onSend` callback because its enabled state and its label are
  // decided by claim state the parent already derives (locked? statement items? validation errors?
  // invoice yet? items still pending?), and a second derivation here would be a second thing to keep
  // in step. This component decides only WHERE it sits.
  sendButton?: React.ReactNode;
  // TAV-2591: wires the "X item(s) require an attachment" line to a toggle link that filters the
  // claim items table down to just those items (via show_attachment_required). Both optional so
  // this stays byte-identical to TAV-2590's rendering wherever the caller doesn't supply them.
  onToggleAttachmentFilter?: () => void;
  attachmentFilterActive?: boolean;
  // TAV-2592: same shape as onToggleAttachmentFilter above, wiring the "N item(s) need a late
  // submission reason" line to a toggle link that filters the claim items table via
  // show_late_submission. Both optional so this stays byte-identical wherever the caller doesn't
  // supply them.
  onToggleLateSubmissionFilter?: () => void;
  lateSubmissionFilterActive?: boolean;
  // TAV-2593: true when the Submit button is present but disabled because one of the three
  // gates below is non-zero. Adds a lead-in sentence to the existing outstanding-counts list
  // rather than inventing a second "why can't I submit" surface.
  submissionBlocked?: boolean;
  // The result of the most recent reconcile, held by the parent. Everything else in this banner is
  // read from persisted claim state so it survives a reload; this deliberately is not, because it
  // describes one action the user just took ("4 updated, 1 removed") rather than the claim's state.
  // Null until they press Send.
  //
  // Its `attachment_reupload` warnings are the reason this prop exists at all: an item whose care
  // recipient, service or delivery date changed had to be recreated at Services Australia, which
  // detaches any attachment already uploaded for it. That is only knowable from the response, and the
  // user has to act on it. Every OTHER warning kind is filtered out - see `syncWarnings` below.
  syncResult?: ClaimApiPushResult | null;
}

// TAV-2593: SA invoice status (Invoices 1.2.0) -> badge copy. Not a lookup - `sa_invoice_status` is
// a raw vendor enum, not a `lookup_sah_claim_statuses` row, so LookupStatusBadge does not apply.
//
// `detail` is the POPOVER body, not an inline line. It was briefly rendered under each bullet and that
// made the banner wordier than the facts in it, so the pill itself became the affordance instead: it
// carries a small info glyph and reveals the meaning on hover or focus. Every string here is the
// vendor spec's own description of that status, condensed - so the explanation the provider reads is
// Services Australia's, not ours.
const SA_INVOICE_STATUS_COPY: Record<string, { label: string; detail?: string; className: string }> = {
  OPEN: {
    label: 'Open',
    detail: 'Items can only be edited while the invoice is Open. It must be submitted before it can be claimed.',
    className: 'bg-secondary',
  },
  SUBMITTED: {
    label: 'Submitted',
    detail: 'Submitted to Services Australia and ready to be claimed.',
    className: 'bg-primary',
  },
  HELD: {
    label: 'Held by Services Australia',
    detail: 'Held internally by Services Australia pending their approval. No action is required by the provider.',
    className: 'bg-info text-dark',
  },
  DELETED: {
    label: 'Deleted',
    detail: 'This invoice has been deleted at Services Australia.',
    className: 'bg-secondary',
  },
  CLAIMED: {
    label: 'Claimed',
    detail: 'Included in a claim, and that claim is in progress.',
    className: 'bg-success',
  },
  COMPLETED: {
    label: 'Completed',
    detail: 'Completed - the claim covering this invoice has been paid.',
    className: 'bg-success',
  },
  // The seventh status, and the only one that is a dead end. Deprecated at Services Australia - no NEW
  // invoice will land here - but historical invoices already in it exist, so the banner has to be able
  // to explain it. The detail is the actionable half: the invoice cannot be revived, the ITEMS have to
  // be claimed again on a different one.
  REJECTED: {
    label: 'Rejected',
    detail:
      'Rejected by Services Australia and cannot be submitted or claimed. Its items must be re-claimed on a new invoice. ' +
      'Services Australia no longer rejects new invoices - only historical ones are in this state.',
    className: 'bg-danger',
  },
};

// TAV-2594: SA claim status (Claim 1.0.0) -> badge copy. A SEPARATE map from SA_INVOICE_STATUS_COPY
// above - two different vendor enums that happen to share some English words. `sa_claim_status`
// keys the Claim 1.0.0 enum (`Being Calculated` / `Pending approval` / `Cancelled` / `Approved` /
// `Completed`), not the Invoices 1.2.0 `sa_invoice_status` enum above. A fresh claim reports
// `Being Calculated`.
const SA_CLAIM_STATUS_COPY: Record<string, { label: string; detail?: string; className: string }> = {
  'Being Calculated': {
    label: 'Being calculated',
    detail: 'Services Australia is calculating this claim.',
    className: 'bg-info text-dark',
  },
  'Pending approval': {
    label: 'Pending approval',
    detail: 'Awaiting approval at Services Australia.',
    className: 'bg-warning text-dark',
  },
  Approved: {
    label: 'Approved',
    detail: 'Approved for payment by Services Australia.',
    className: 'bg-success',
  },
  Completed: { label: 'Completed', detail: 'Completed - this claim has been paid.', className: 'bg-success' },
  Cancelled: {
    label: 'Cancelled',
    detail: 'Cancelled or rejected by Services Australia.',
    className: 'bg-secondary',
  },
};

/**
 * ClaimApiPushSummary
 *
 * The claim's entire Services Australia relationship, in one banner: the invoice and the claim it was
 * swept into with their statuses, how many items have been sent, whatever Services Australia is still
 * waiting on, and the Send / Sync action itself.
 *
 * Renders for any PRODA-connected company - including before anything has been sent, which is the one
 * moment the user most needs a prompt. A flat-file provider gets `null` and a byte-identical page.
 *
 * Each status pill is a hover/focus popover explaining what that vendor status MEANS, because the
 * enums are not self-explanatory and the consequences differ sharply between them (a HELD invoice
 * needs nothing from the provider; a REJECTED one can never be submitted and its items have to be
 * re-claimed elsewhere).
 */
const ClaimApiPushSummary: React.FC<ComponentProps> = ({
  claim,
  sendButton = null,
  onToggleAttachmentFilter,
  attachmentFilterActive = false,
  onToggleLateSubmissionFilter,
  lateSubmissionFilterActive = false,
  submissionBlocked = false,
  syncResult = null,
}) => {
  // Renders for any PRODA-CONNECTED company, invoice or not - the banner is now the home of the whole
  // Services Australia relationship for this claim, including the Send action and the "nothing has
  // been sent yet" state. It used to key off `sa_invoice_id` alone, which meant the one moment the
  // user most needs a prompt (before anything exists) was the one moment it rendered nothing.
  //
  // `sa_invoice_id` is still honoured as a fallback so a claim that HAS an invoice always shows its
  // state, even if the company's connection is later switched off. A flat-file provider satisfies
  // neither and gets a byte-identical page to before.
  if (!claim.sa_connected && !claim.sa_invoice_id) return null;

  const hasInvoice = Boolean(claim.sa_invoice_id);
  const itemsPushed = claim.items_pushed ?? 0;
  const itemsPendingPush = claim.items_pending_push ?? 0;
  const itemsAttachmentRequired = claim.items_attachment_required ?? 0;
  const itemsFurtherInfoRequired = claim.items_further_info_required ?? 0;
  const itemsLateSubmissionRequired = claim.items_late_submission_required ?? 0;
  const messages = claim.sa_meta_information?.messages ?? [];
  // HELD is deliberately excluded from this computation. Per Invoices 1.2.0 it is a benign
  // in-flight state ("no action is required by provider"), so it must not turn the banner red or
  // imply the provider must do something - it is surfaced separately, informationally, by the
  // SA_INVOICE_STATUS_COPY badge below.
  const needsAttention =
    itemsPendingPush > 0 ||
    itemsAttachmentRequired > 0 ||
    itemsFurtherInfoRequired > 0 ||
    itemsLateSubmissionRequired > 0;
  const saStatus = claim.sa_invoice_status ?? null;
  const statusCopy = saStatus ? SA_INVOICE_STATUS_COPY[saStatus] : undefined;
  // TAV-2594. A different axis from saStatus/statusCopy above - see SA_CLAIM_STATUS_COPY.
  const saClaimStatus = claim.sa_claim_status ?? null;
  const claimStatusCopy = saClaimStatus ? SA_CLAIM_STATUS_COPY[saClaimStatus] : undefined;
  // Deliberately NOT the same test as needsAttention. That one drives the banner's colour and icon,
  // where a pending push legitimately counts as "not all clear". This one decides only whether the
  // attention list renders AT ALL, and a pending push produces no <li> of its own - the count is
  // already on the "X of Y items sent" line and on the retry button. Conflating the two rendered an
  // empty bullet list.
  const hasAttentionDetail =
    itemsAttachmentRequired > 0 || itemsFurtherInfoRequired > 0 || itemsLateSubmissionRequired > 0;
  // "Lodged" - the invoice has left OPEN, so Services Australia refuses item edits and there is
  // nothing left to re-send. Everything short of that (draft AND finalised) can still be reconciled,
  // which is exactly the set /send-to-sa admits server-side.
  //
  // TAV-2595 replaced the `isClaimSubmitted || isClaimClaiming` pair with the predicate, because that
  // pair stopped being total the moment four downstream SA statuses existed: they are all past the same
  // lodgement, yet fell through it and rendered a dead Send button whose disabled reason read
  // "unfinalise the claim first" - advice /unfinalise refuses for exactly those tags.
  const isLodged = isClaimLodged(claim.status?.tag);
  // ONLY the re-upload warnings. Everything else syncClaimToApi returns is either already visible
  // where it belongs or not the user's problem to solve here:
  //
  //   - a rejected row (`item_rejected`) carries its parsed vendor error on its OWN row, in the push
  //     icon's popover. Repeating it at claim level turned this banner into a wall of raw JSON on any
  //     claim with a mix of accepted and rejected items, burying the counts the banner exists for.
  //   - `item_incomplete` and `invoice` are diagnostics; they stay in the response and the toast.
  //
  // `attachment_reupload` is the exception because nothing else on the page can say it: the file was
  // silently detached when Services Australia forced the item to be recreated, so without this line
  // the user has no way to learn their evidence needs uploading again.
  const syncWarnings = (syncResult?.warnings ?? []).filter((warning) => warning.type === 'attachment_reupload');

  /**
   * A status pill that explains itself. The vendor enums are not self-describing and their
   * consequences differ sharply - a HELD invoice needs nothing from the provider, a REJECTED one can
   * never be submitted and its items have to be re-claimed on a different invoice - so the meaning has
   * to be reachable without leaving the page or reading the spec.
   *
   * Built on HoverPopover rather than a fresh hover implementation. That component already
   * solves the hard parts once for this feature: opening on hover AND focus (so it is not mouse-only),
   * a grace period on the way out (the body portals to document.body, so it is not a DOM descendant of
   * the trigger and a synchronous close beats the pointer to it), tap-to-toggle on touch devices where
   * hover does not exist at all, and the aria-expanded / aria-describedby wiring. Its docstring says it
   * was extracted so two callers could not drift apart - a third has the same claim on it.
   *
   * `variant: 'info'` for the header: this is explanatory, not a warning. Falls back to a plain
   * non-interactive badge when a status has no `detail` - an UNKNOWN vendor value renders raw, and a
   * pill promising an explanation it does not have is worse than a pill that never offered one.
   */
  const renderStatusPill = ({
    idSuffix,
    label,
    className,
    objectName,
    detail,
    dataCy,
  }: {
    idSuffix: string;
    label: string;
    className: string;
    /** 'Invoice' or 'Claim' - reaches the accessible name only, never the visible pill. */
    objectName: string;
    detail?: string;
    dataCy: string;
  }) => {
    if (!detail) {
      return (
        <span className={`badge ms-2 ${className}`} data-cy={dataCy}>
          {label}
        </span>
      );
    }
    return (
      <span className="ms-2" data-cy={dataCy}>
        <HoverPopover
          id={`${claim.id}-${idSuffix}`}
          icon={faInfoCircle}
          label={label}
          // NO `btn` and NO `p-0`. `.badge` supplies the padding, radius, weight and font size, and
          // both of those classes fight it: `p-0` wins over `.badge`'s own padding (utility vs
          // component) and flattened the pill to its text, while `.btn` brings a competing padding of
          // its own. All the button needs is its chrome removed - Bootstrap's reboot already makes a
          // <button> inherit font-family and line-height, and the `bg-*` in `className` covers the UA
          // default background.
          triggerBaseClassName="border-0 align-baseline"
          triggerClassName={`badge ${className}`}
          variant="info"
          // The status itself is the title. "Invoice status" / "Claim status" was a label for
          // something the reader can already see - which line they are hovering - so it spent the
          // heading on nothing. The body then says what the status MEANS, which is the whole point.
          header={label}
          messages={[detail]}
          // The aria-label still carries WHICH object, because a screen-reader user hearing only
          // "Claimed, show what this means" has lost the invoice/claim distinction the sighted reader
          // gets from position.
          ariaLabel={`${objectName} status: ${label}. Show what this means.`}
          dataCyPrefix={`${dataCy}-info`}
          // Opens right: these pills sit at the start of a wide banner, so there is room to the right
          // and the popover does not cover the line it explains.
          placement="right"
        />
      </span>
    );
  };
  // THREE states now, not two. Background/border track the state; text stays black throughout.
  //
  // The neutral state is the new one, and it needs to be neutral rather than green: with no invoice
  // every count is zero, so the old two-state logic would have declared an untouched claim "all
  // clear" with a green tick - the most misleading thing it could say about a claim that has not been
  // sent. It uses Bootstrap utilities rather than a third `.x-box` class because `App.css` is a built
  // artifact and the utilities are already in it.
  let boxClass = 'success-box border-success';
  let iconClass = 'text-success';
  let icon = faCircleCheck;
  if (!hasInvoice) {
    boxClass = 'bg-light border-secondary';
    iconClass = 'text-secondary';
    icon = faInfoCircle;
  } else if (needsAttention) {
    boxClass = 'error-box border-danger';
    // Yellow on the red banner, green on the green one - both read clearly against their tint.
    iconClass = 'text-warning';
    icon = faTriangleExclamation;
  }

  return (
    <div className={`d-flex align-items-center ${boxClass} rounded p-3 mb-3 border`} data-cy="claim-api-push-summary">
      <Icon icon={icon} className={`${iconClass} me-3`} />
      <div className="d-flex flex-column text-black">
        {/*
          ONE heading for the whole relationship, with the two Services Australia objects listed
          beneath it. They used to be two sibling headings that each repeated "Services Australia",
          which read as two unrelated banners stacked together rather than one account of this claim.

          `h4` with the `.h6` size utility, not a literal `<h6>`: the claim page's ContainerHeader is
          an `<h3>`, so jumping straight to h6 is a heading-order accessibility violation. The class
          keeps the visual size identical.
        */}
        <h4 className="h6 fw-bold text-black mb-1">Services Australia</h4>
        {hasInvoice ? (
          // `ps-3` with visible markers, matching the banner's other lists - these are two items of
          // one set, and the markers are what make that read at a glance.
          <ul className="mb-0 ps-3" data-cy="claim-sa-objects">
            {/*
              The invoice and the claim are two DIFFERENT vendor objects on two different lifecycles
              (Invoices 1.2.0 vs Claim 1.0.0), which is why each carries its own status badge from its
              own copy map. The claim line is gated on `sa_claim_id` rather than on its status, so a
              lodged claim keeps its line even in the should-not-happen case the status is missing.
            */}
            <li>
              <span className="fw-medium">Invoice {claim.sa_invoice_id}</span>
              {renderStatusPill({
                idSuffix: 'invoice',
                label: statusCopy?.label ?? saStatus ?? 'Unknown',
                className: statusCopy?.className ?? 'bg-secondary',
                objectName: 'Invoice',
                detail: statusCopy?.detail,
                dataCy: 'claim-sa-invoice-status',
              })}
            </li>
            {claim.sa_claim_id && (
              <li data-cy="claim-sa-claim-status-heading">
                {/*
                  "In Claim", not "Claim". A Services Australia claim is PROVIDER-scoped: the POST
                  takes no body and sweeps up every invoice the provider has submitted and not yet
                  claimed, so this claim's invoice is one of possibly many inside it. "Claim 12345"
                  read as though TurnPoint's claim and SA's claim were the same object, which is the
                  structural mismatch at the heart of TAV-2594.
                */}
                <span className="fw-medium">In Claim {claim.sa_claim_id}</span>
                {renderStatusPill({
                  idSuffix: 'claim',
                  label: claimStatusCopy?.label ?? saClaimStatus ?? 'Unknown',
                  className: claimStatusCopy?.className ?? 'bg-secondary',
                  objectName: 'Claim',
                  detail: claimStatusCopy?.detail,
                  dataCy: 'claim-sa-claim-status',
                })}
                {/*
                  TAV-2595. Only present once Services Australia has paid the claim (Claim 1.0.0
                  `paymentDate` - "Field will not be returned if Claim has not been paid"), so this
                  is the moment the claim's lifecycle actually ends, not merely `Approved`.
                */}
                {claim.payment_date && (
                  <span className="d-block fw-normal" data-cy="claim-sa-payment-date">
                    Paid {dayjs(claim.payment_date).format(dateDisplayFormat)}
                  </span>
                )}
                {/*
                  TAV-3234 (AC2). Only present once the claim has been lodged - sa_last_synced_at is
                  flattened from company_sa_claims.last_synced_at, and that row only exists after
                  /claims/submit-claim. Before lodgement the only thing that changes Services
                  Australia's state is the user pressing Sync themselves, so there is nothing to
                  report; after lodgement Services Australia moves the claim through its own overnight
                  batch, which is exactly when the user cannot otherwise tell how current this is.
                */}
                {claim.sa_last_synced_at && (
                  <span className="d-block fw-normal text-muted small" data-cy="claim-sa-last-synced-at">
                    Status last checked {dayjs(claim.sa_last_synced_at).format(dateTimeDisplayFormat)}
                  </span>
                )}
                {/*
                  TAV-2595 (AC #4). Services Australia's CLAIM-level diagnostics, nested under the
                  claim line they belong to. Deliberately NOT merged with the invoice-level
                  `messages` list further down: that array is the Invoices 1.2.0 axis, this one is
                  Claim 1.0.0, and a reader needs to know which vendor object is complaining. Same
                  shortText/longText de-duplication as that list - Services Australia routinely
                  repeats the short text verbatim in the long text.
                */}
                {(claim.sa_claim_messages?.length ?? 0) > 0 && (
                  <ul className="mb-0 mt-1 ps-3 fw-normal" data-cy="claim-sa-claim-messages">
                    {claim.sa_claim_messages?.map((message, index) => {
                      const heading = message.shortText ?? message.longText ?? message.type;
                      const detail = message.longText && message.longText !== heading ? message.longText : null;
                      return (
                        <li key={`sa-claim-message-${message.type ?? 'message'}-${index}`}>
                          {heading}
                          {detail && <span className="d-block">{detail}</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            )}
          </ul>
        ) : (
          // The pre-invoice state. Says what is true and what to do about it, in that order - the
          // Send button sitting alongside is the "what to do".
          <span className="fw-normal" data-cy="claim-sa-no-invoice">
            No invoice created. Please send to Services Australia to start the claim.
          </span>
        )}
        {/*
          "to Services Australia" dropped from this line: the heading above now says it, and repeating
          it made the sentence longer than the fact it carries.
        */}
        {hasInvoice && (
          <span className="fw-medium mt-1" data-cy="claim-api-push-items-sent">
            {itemsPushed} of {itemsPushed + itemsPendingPush} items sent.
          </span>
        )}
        {/*
          Two independent lists, ordered rather than mutually exclusive.

          They were mutually exclusive until TAV-2592 review: the attention list won the slot and
          suppressed the SA messages. That inverted the intent, because the API fetches `messages[]`
          ONLY when an attention item exists (claims.mjs - `if (attentionItems.length)`), so the
          branch labelled "AC #7: the specific detail of WHAT is missing" was unreachable in every
          state SA actually populates it for. A push returning `furtherInformationRequired` showed
          "1 item requires further information." while SA's own WARN naming the missing field was
          fetched, persisted and then thrown away.

          Ordering solves what the exclusion was reaching for. SA's messages are often bare
          confirmations ("Invoice 1000113321 created"), and the original worry was those burying the
          actionable item - so the actionable items go FIRST and the messages sit underneath as
          supporting detail. Nothing is discarded to achieve it.
        */}
        {hasAttentionDetail && (
          /*
            The lead-in ends in a colon, so whatever sits visually below it reads as the list it
            names - and SA's messages[] render immediately after this block. The two co-occur by
            construction in exactly this state: the API only fetches messages[] when an attention
            item exists, and submissionBlocked is only true when one does. Both lists are plain
            `fw-medium` bullets, so with no boundary an informational "Invoice 1000113321 created"
            reads as a submission blocker.

            The left rule binds the sentence to ITS list and stops there, leaving the messages
            plainly outside it. Applied ONLY when blocked: with no lead-in there is nothing to
            scope, and the unstyled wrapper leaves the non-blocked rendering identical to TAV-2592's
            (a flex item establishes its own formatting context, so the list's `mt-2` still spaces
            it exactly as it did when the <ul> was the flex item itself).
          */
          <div
            className={submissionBlocked ? 'border-start border-2 border-danger ps-2 mt-2' : undefined}
            data-cy="claim-api-push-attention-group"
          >
            {submissionBlocked && (
              <span
                className="fw-medium"
                id={`claim-api-push-submit-blocked-${claim.id}`}
                data-cy="claim-api-push-submit-blocked"
              >
                This invoice cannot be submitted to Services Australia until:
              </span>
            )}
            {/*
              The left rule above is the VISUAL half of the grouping; this is the programmatic half.
              Without it a screen reader enumerating the page reaches "list, 3 items" with the
              framing sentence left behind as a detached sibling - so the three requirements are
              announced with nothing marking them as submission blockers. Naming the <ul> makes it
              announce as "This invoice cannot be submitted... , list, 3 items".

              Only when blocked: with no lead-in there is no element to point at, and a dangling
              `aria-labelledby` is worse than none. The id is keyed on `claim.id`, which is always
              present - `sa_invoice_id` is not, since the render gate above now also admits a
              connected claim that has no invoice yet.
            */}
            <ul
              className="mb-0 mt-2 ps-3"
              aria-labelledby={submissionBlocked ? `claim-api-push-submit-blocked-${claim.id}` : undefined}
              data-cy="claim-api-push-attention"
            >
              {/*
              No per-line icons: the banner already carries one state icon, and repeating it on
              every bullet added noise without adding meaning - the list marker and the banner's
              colour already say "these are the problems".
            */}
              {itemsAttachmentRequired > 0 && (
                <li className="fw-medium">
                  {itemsAttachmentRequired} item{itemsAttachmentRequired === 1 ? ' requires' : 's require'} an
                  attachment.
                  {onToggleAttachmentFilter && (
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 ms-2 align-baseline"
                      // Both toggles render the same two words, so the visible text alone leaves two
                      // buttons with an identical accessible name - the sentence they sit in is not
                      // part of it, so a screen reader enumerating buttons cannot tell them apart.
                      aria-label={
                        attachmentFilterActive
                          ? 'Show all items instead of only items requiring an attachment'
                          : 'View only items requiring an attachment'
                      }
                      onClick={onToggleAttachmentFilter}
                      dataCy="claim-api-push-attachment-filter"
                    >
                      {attachmentFilterActive ? 'Show all items' : 'View items'}
                    </Button>
                  )}
                </li>
              )}
              {itemsFurtherInfoRequired > 0 && (
                <li className="fw-medium">
                  {itemsFurtherInfoRequired} item{itemsFurtherInfoRequired === 1 ? ' requires' : 's require'} further
                  information.
                </li>
              )}
              {itemsLateSubmissionRequired > 0 && (
                <li className="fw-medium">
                  {itemsLateSubmissionRequired} item{itemsLateSubmissionRequired === 1 ? ' needs' : 's need'} a late
                  submission reason.
                  {onToggleLateSubmissionFilter && (
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 ms-2 align-baseline"
                      // See the attachment toggle above - without an explicit name these two buttons
                      // are indistinguishable to assistive technology.
                      aria-label={
                        lateSubmissionFilterActive
                          ? 'Show all items instead of only items needing a late submission reason'
                          : 'View only items needing a late submission reason'
                      }
                      onClick={onToggleLateSubmissionFilter}
                      dataCy="claim-api-push-late-submission-filter"
                    >
                      {lateSubmissionFilterActive ? 'Show all items' : 'View items'}
                    </Button>
                  )}
                </li>
              )}
            </ul>
          </div>
        )}
        {/*
          AC #7: the specific detail of WHAT is missing. Services Australia returns this as
          invoice-level `messages[]` rather than per-item - `Item` carries only the boolean
          `furtherInformationRequired` - so it cannot be attributed to a single row and is listed
          here instead of in the claim-items table. Read from the persisted
          `claim.sa_meta_information` (written by the API after each push) so it survives a page
          reload rather than living only in the finalise response.
        */}
        {messages.length > 0 && (
          <ul className="mb-0 mt-2 ps-3" data-cy="claim-api-push-messages">
            {messages.map((message, index) => {
              const heading = message.shortText ?? message.longText ?? message.type;
              // Services Australia routinely puts the SAME string in shortText and longText for
              // simple informational messages (e.g. "Invoice 1000113317 created"), so longText is
              // only rendered when it actually adds something - otherwise the banner printed the
              // identical line twice.
              const detail = message.longText && message.longText !== heading ? message.longText : null;
              return (
                <li key={`${message.type ?? 'message'}-${index}`} className="fw-medium">
                  {heading}
                  {/*
                    `fw-normal` (not a muted grey) is what de-emphasises the long text relative to
                    the short one - the banner's text is black throughout, so weight rather than
                    colour carries the hierarchy.
                  */}
                  {detail && <span className="d-block fw-normal">{detail}</span>}
                </li>
              );
            })}
          </ul>
        )}
        {/*
          Re-upload warnings from the reconcile that just ran (filtered above - see `syncWarnings`).
          Kept separate from `messages`, which are Services Australia's own invoice-level diagnostics:
          these are TurnPoint's, and this is the one kind that names an action nothing else on the
          page can. Changing an item's care recipient, service or delivery date forces a delete +
          recreate at Services Australia (the vendor forbids updating those three), which detaches any
          file already uploaded against the old item id.
        */}
        {syncWarnings.length > 0 && (
          <ul className="mb-0 mt-2 ps-3" data-cy="claim-api-push-sync-warnings">
            {syncWarnings.map((warning, index) => (
              <li key={`${warning.reference ?? 'warning'}-${index}`} className="fw-medium">
                {warning.reference && <span className="fw-normal">{warning.reference} - </span>}
                {warning.reason}
              </li>
            ))}
          </ul>
        )}
      </div>
      {/*
        The Send / Sync action, moved here from the page toolbar. This is where it belongs: it acts
        on the Services Australia relationship this banner describes, and in the pre-invoice state the
        prompt and the button that answers it need to be the same thing the eye lands on.

        Suppressed once the claim is LODGED - the invoice has left OPEN, Services Australia refuses
        item edits, and there is nothing left to send. `ms-auto` pushes it to the banner's right edge.
      */}
      {!isLodged && sendButton && <div className="ms-auto ps-3">{sendButton}</div>}
    </div>
  );
};

export default ClaimApiPushSummary;
